import { Progress } from "@/components/ui/progress";
import { Card } from "@/components/ui/card";
import { useCreditsUsage } from "@/hooks/useCreditsUsage";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/context/AuthContext";
import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { LimitReachedDialog } from "@/components/LimitReachedDialog";
import { PurchasedCreditsInfo } from "@/components/PurchasedCreditsInfo";
import { CreditUsageDetailsDialog } from "@/components/credits/CreditUsageDetailsDialog";
import { Coins } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";

export const CreditsUsage = ({ compact = false, variant }: { compact?: boolean; variant?: 'icon' }) => {
  const { creditUsage, loading, formatCredits, refetch } = useCreditsUsage();
  const { user } = useAuth();
  const [planType, setPlanType] = useState<string>('free');
  const [showLimitDialog, setShowLimitDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const fetchPlanType = async () => {
    if (!user) return;
    
    const { data: subscription } = await supabase
      .from('subscriptions')
      .select('plan_type')
      .eq('user_id', user.id)
      .eq('status', 'active')
      .order('created_at', { ascending: false })
      .limit(1)
      .maybeSingle();
      
    if (subscription) {
      setPlanType(subscription.plan_type);
    }
  };

  useEffect(() => {
    fetchPlanType();
  }, [user]);

  useEffect(() => {
    if (!user) return;

    const timestamp = Date.now();

    const subscriptionChannel = supabase
      .channel(`credits-subscription-${user.id}-${timestamp}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'subscriptions',
          filter: `user_id=eq.${user.id}`
        },
        (payload) => {
          console.log('CreditsUsage: Subscription change detected:', payload);
          if (payload.new && typeof payload.new === 'object' && 'plan_type' in payload.new) {
            setPlanType(payload.new.plan_type as string);
          }
          fetchPlanType();
        }
      )
      .subscribe();

    const creditsChannel = supabase
      .channel(`credits-usage-${user.id}-${timestamp}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'user_credits',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          console.log('CreditsUsage: Credits change detected, refetching...');
          refetch();
        }
      )
      .subscribe();

    const purchasedCreditsChannel = supabase
      .channel(`purchased-credits-${user.id}-${timestamp}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'purchased_credits',
          filter: `user_id=eq.${user.id}`
        },
        () => {
          console.log('CreditsUsage: Purchased credits change detected, refetching...');
          refetch();
        }
      )
      .subscribe();

    return () => {
      supabase.removeChannel(subscriptionChannel);
      supabase.removeChannel(creditsChannel);
      supabase.removeChannel(purchasedCreditsChannel);
    };
  }, [user, refetch]);

  useEffect(() => {
    if (!creditUsage) return;
    
    const progressValue = creditUsage.percentage;
    const isAtLimit = progressValue >= 100;
    
    if (progressValue >= 90 && !isAtLimit && planType !== 'premium') {
      setShowLimitDialog(true);
    }
  }, [creditUsage, planType]);

  const getPlanDisplayName = (plan: string) => {
    switch (plan) {
      case 'premium': return 'Premium';
      case 'pro': return 'Pro';
      case 'basic': return 'Básico';
      case 'free':
      default: return 'Gratuito';
    }
  };

  if (loading) {
    if (variant === 'icon') {
      return (
        <div className="h-9 w-9 rounded-full bg-muted animate-pulse" />
      );
    }
    return (
      <Card className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <span className="text-sm font-medium">Carregando créditos...</span>
          <div className="w-16 h-4 bg-gray-200 animate-pulse rounded"></div>
        </div>
        <Progress value={0} className="h-2" />
        <div className="text-xs text-muted-foreground">
          <div className="w-32 h-3 bg-gray-200 animate-pulse rounded"></div>
        </div>
      </Card>
    );
  }

  if (!creditUsage) {
    return null;
  }

  const progressValue = creditUsage.percentage;
  const isNearLimit = progressValue >= 80;
  const isAtLimit = progressValue >= 100;

  // Icon variant for collapsed sidebar
  if (variant === 'icon') {
    const color = isAtLimit ? 'text-destructive' : isNearLimit ? 'text-yellow-500' : 'text-primary';
    return (
      <>
        <LimitReachedDialog 
          open={showLimitDialog}
          onOpenChange={setShowLimitDialog}
          type="credits"
        />
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => setShowDetailsDialog(true)}
              className="relative flex items-center justify-center h-9 w-9 rounded-lg hover:bg-sidebar-accent transition-colors"
            >
              <Coins className={`h-5 w-5 ${color}`} />
              <span className={`absolute -bottom-0.5 -right-0.5 text-[8px] font-bold ${color} bg-sidebar rounded-full px-0.5 leading-none`}>
                {Math.round(progressValue)}%
              </span>
            </button>
          </TooltipTrigger>
          <TooltipContent side="right" sideOffset={8}>
            <div className="text-xs">
              <div className="font-medium">Créditos ({getPlanDisplayName(planType)})</div>
              <div>{formatCredits(creditUsage.total_credits)} / {formatCredits(creditUsage.limit_credits)}</div>
            </div>
          </TooltipContent>
        </Tooltip>
        <CreditUsageDetailsDialog
          open={showDetailsDialog}
          onOpenChange={setShowDetailsDialog}
        />
      </>
    );
  }

  if (compact) {
    return (
      <>
        <LimitReachedDialog 
          open={showLimitDialog}
          onOpenChange={setShowLimitDialog}
          type="credits"
        />
        <Card 
          className="p-2 space-y-1 cursor-pointer hover:shadow-lg transition-shadow"
          onClick={() => setShowDetailsDialog(true)}
        >
          <Progress 
            value={progressValue} 
            showPercentageInside={true}
            className={`h-4 ${isAtLimit ? 'bg-red-100' : isNearLimit ? 'bg-yellow-100' : 'bg-green-100'}`}
          />
          <div className="flex items-center justify-between gap-1">
            <div className="flex items-center gap-1 min-w-0">
              <span className="text-[10px] font-medium truncate">Créditos</span>
              <Badge variant={planType === 'free' ? 'secondary' : 'default'} className="text-[9px] px-1 py-0 h-4 whitespace-nowrap">
                {getPlanDisplayName(planType)}
              </Badge>
            </div>
          </div>
          <span className="text-[10px] text-muted-foreground block">
            {formatCredits(creditUsage.total_credits)} / {formatCredits(creditUsage.limit_credits)}
          </span>
        </Card>
        <CreditUsageDetailsDialog
          open={showDetailsDialog}
          onOpenChange={setShowDetailsDialog}
        />
      </>
    );
  }

  return (
    <>
      <LimitReachedDialog 
        open={showLimitDialog}
        onOpenChange={setShowLimitDialog}
        type="credits"
      />
      <Card 
        className="p-3 space-y-2 cursor-pointer hover:shadow-lg transition-shadow" 
        onClick={() => setShowDetailsDialog(true)}
      >
      <Progress 
        value={progressValue} 
        showPercentageInside={true}
        className={`${isAtLimit ? 'bg-red-100' : isNearLimit ? 'bg-yellow-100' : 'bg-green-100'}`}
      />
      
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium">Créditos</span>
          <Badge variant={planType === 'free' ? 'secondary' : 'default'} className="text-xs whitespace-nowrap">
            {getPlanDisplayName(planType)}
          </Badge>
        </div>
        <span className="text-xs text-muted-foreground">
          {formatCredits(creditUsage.total_credits)} / {formatCredits(creditUsage.limit_credits)}
        </span>
      </div>
      
      {(isAtLimit || isNearLimit) && (
        <div className="flex justify-center">
          {isAtLimit && (
            <Badge variant="destructive" className="text-xs">
              Limite atingido
            </Badge>
          )}
          {isNearLimit && !isAtLimit && (
            <Badge variant="secondary" className="text-xs">
              Próximo do limite
            </Badge>
          )}
        </div>
      )}
      </Card>

      <CreditUsageDetailsDialog
        open={showDetailsDialog}
        onOpenChange={setShowDetailsDialog}
      />
    
      <PurchasedCreditsInfo 
        purchasedCredits={creditUsage.purchased_credits}
        creditsDetails={creditUsage.purchased_credits_details}
      />
    </>
  );
};
